import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactPreview } from '../../shared/models';
import { ChatStoreService } from '../../shared/chat-store.service';
import { formatPhoneNumber } from '../../shared/phone-format';

@Component({
  standalone: true,
  selector: 'app-contact-list',
  imports: [CommonModule],
  template: `
  <div class="list">
    <button class="row"
      *ngFor="let c of contacts"
      [class.active]="c.conversationId===selectedId"
      (click)="select.emit(c.conversationId)">
      <div class="avatar" aria-hidden="true">
        <svg class="avatarIcon" viewBox="0 0 24 24" fill="none">
          <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z" stroke="currentColor" stroke-width="1.8"/>
          <path d="M5 20a7 7 0 0 1 14 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </div>

      <div class="mid">
        <div class="top">
          <div class="topLeft">
            <div class="title">{{ formatPhone(c.phone) }}</div>
            <div class="name">{{ c.displayName || 'Unknown contact' }}</div>
          </div>
          <div class="time">{{ store.hhmm(c.messageTs) }}</div>
        </div>
        <div class="bottom">
          <div class="last">{{ c.lastMessageType === 'text' ? c.lastMessage : c.lastMessageType.toUpperCase() }}</div>
          <div class="right">
            <span class="humanAlertIcon" *ngIf="c.requiresHumanAgent" title="Human required">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 4.5 20 19.5H4L12 4.5Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                <path d="M12 9v4.5M12 16.8h.01" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </span>
            <span
              class="windowIcon"
              [class.available]="replyWindowState(c.messageTs)==='available'"
              [class.expiring]="replyWindowState(c.messageTs)==='expiring'"
              [class.expired]="replyWindowState(c.messageTs)==='expired'"
              [attr.title]="replyWindowTooltip(c.messageTs)">
              <svg class="windowGlyph" viewBox="0 0 24 24" fill="none" *ngIf="replyWindowState(c.messageTs)==='available'">
                <path d="M4.5 7.5a3 3 0 0 1 3-3h9a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-6l-4 3v-3a3 3 0 0 1-2-3z" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/>
              </svg>
              <svg class="windowGlyph" viewBox="0 0 24 24" fill="none" *ngIf="replyWindowState(c.messageTs)==='expiring'">
                <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="2"/>
                <path d="M12 7.5V12l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg class="windowGlyph" viewBox="0 0 24 24" fill="none" *ngIf="replyWindowState(c.messageTs)==='expired'">
                <path d="M12 5.5a7.5 7.5 0 1 1-5.3 2.2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M7 4.5v4h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </div>
        <div class="meta">
          <span class="label"
            [class.sales]="c.mainCategory==='Sales'"
            [class.lead]="c.mainCategory==='Lead'"
            *ngIf="c.label">{{ c.label }}</span>
        </div>
      </div>
    </button>

    <div class="empty" *ngIf="!contacts.length">No results.</div>
  </div>
  `,
  styles: [`
    :host{display:block;height:100%;min-height:0}
    .list{height:100%;overflow:auto;padding:6px}
    .list{scrollbar-width:thin;scrollbar-color:rgba(134,148,161,.55) rgba(17,27,33,.75)}
    .list::-webkit-scrollbar{width:10px}
    .list::-webkit-scrollbar-track{
      background:rgba(17,27,33,.75);
      border-radius:999px;
    }
    .list::-webkit-scrollbar-thumb{
      background:linear-gradient(180deg, rgba(134,148,161,.7), rgba(101,115,126,.75));
      border:2px solid rgba(17,27,33,.8);
      border-radius:999px;
    }
    .list::-webkit-scrollbar-thumb:hover{
      background:linear-gradient(180deg, rgba(160,174,186,.8), rgba(118,132,145,.85));
    }
    :host-context(body.light) .list{scrollbar-color:rgba(100,116,139,.55) rgba(226,232,240,.7)}
    :host-context(body.light) .list::-webkit-scrollbar-track{
      background:rgba(226,232,240,.7);
    }
    :host-context(body.light) .list::-webkit-scrollbar-thumb{
      background:linear-gradient(180deg, rgba(148,163,184,.75), rgba(100,116,139,.7));
      border-color:rgba(226,232,240,.8);
    }
    .row{width:100%;display:flex;gap:12px;align-items:flex-start;padding:10px;border-radius:16px;border:1px solid transparent;background:transparent;text-align:left;cursor:pointer;color:inherit}
    .row:hover{background:rgba(42,57,66,.45);border-color:rgba(42,57,66,.55)}
    :host-context(body.light) .row:hover{background:rgba(0,0,0,.03);border-color:rgba(0,0,0,.06)}
    .row.active{background:rgba(0,168,132,.10);border-color:rgba(0,168,132,.22)}
    .avatar{width:44px;height:44px;border-radius:16px;display:flex;align-items:center;justify-content:center;background:rgba(32,44,51,.55);border:1px solid var(--border);color:rgba(148,163,184,.95)}
    .avatarIcon{width:22px;height:22px;display:block}
    :host-context(body.light) .avatar{background:rgba(0,0,0,.05)}
    .mid{flex:1;min-width:0}
    .top{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}
    .topLeft{min-width:0}
    .title{font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .name{margin-top:2px;color:var(--muted);font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .time{font-size:12px;color:var(--muted)}
    .bottom{display:flex;justify-content:space-between;gap:10px;align-items:center;margin-top:4px}
    .right{display:flex;align-items:center;gap:8px}
    .last{color:var(--muted);font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .humanAlertIcon{
      width:22px;
      height:22px;
      border-radius:999px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      border:1px solid rgba(217,119,6,.45);
      background:rgba(217,119,6,.16);
      color:rgb(253,186,116);
      flex:0 0 auto;
    }
    .humanAlertIcon svg{
      width:13px;
      height:13px;
      stroke:currentColor;
      stroke-linecap:round;
      stroke-linejoin:round;
    }
    :host-context(body.light) .humanAlertIcon{
      color:rgb(154,52,18);
      background:rgba(251,146,60,.22);
      border-color:rgba(217,119,6,.45);
    }
    .windowIcon{
      width:22px;
      height:22px;
      border-radius:999px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      border:1px solid transparent;
    }
    .windowGlyph{width:13px;height:13px;display:block}
    .windowIcon.available{
      border-color:rgba(34,197,94,.35);
      background:rgba(34,197,94,.10);
      color:rgb(134,239,172);
    }
    .windowIcon.expiring{
      border-color:rgba(245,158,11,.35);
      background:rgba(245,158,11,.12);
      color:rgb(253,230,138);
    }
    .windowIcon.expired{
      border-color:rgba(100,116,139,.4);
      background:rgba(100,116,139,.14);
      color:rgb(148,163,184);
    }
    :host-context(body.light) .windowIcon.available{
      background:rgba(34,197,94,.14);
      border-color:rgba(22,163,74,.35);
      color:rgb(21,128,61);
    }
    :host-context(body.light) .windowIcon.expiring{
      background:rgba(251,191,36,.20);
      border-color:rgba(217,119,6,.35);
      color:rgb(146,64,14);
    }
    :host-context(body.light) .windowIcon.expired{
      background:rgba(100,116,139,.12);
      border-color:rgba(100,116,139,.28);
      color:rgb(71,85,105);
    }
    .meta{margin-top:6px}
    .label{display:inline-flex;font-size:11px;padding:3px 8px;border-radius:999px;border:1px solid transparent}
    .label.sales{border-color:rgba(37,99,235,.35);background:rgba(37,99,235,.14);color:rgb(147,197,253)}
    .label.lead{border-color:rgba(22,163,74,.35);background:rgba(22,163,74,.14);color:rgb(134,239,172)}
    :host-context(body.light) .label.sales{
      color:rgb(30,64,175);
      background:rgba(59,130,246,.20);
      border-color:rgba(37,99,235,.45);
    }
    :host-context(body.light) .label.lead{
      color:rgb(22,101,52);
      background:rgba(34,197,94,.22);
      border-color:rgba(22,163,74,.45);
    }
    .empty{padding:14px;color:var(--muted);text-align:center}
  `]
})
export class ContactListComponent {
  @Input() contacts: ContactPreview[] = [];
  @Input() selectedId: string | null = null;
  @Output() select = new EventEmitter<string>();

  constructor(public store: ChatStoreService) {}

  formatPhone(value: string): string {
    return formatPhoneNumber(value);
  }

  replyWindowState(timestamp: string): 'available' | 'expiring' | 'expired' {
    const ts = new Date(timestamp).getTime();
    if (Number.isNaN(ts)) return 'available';

    const elapsedMs = Date.now() - ts;
    const dayMs = 24 * 60 * 60 * 1000;
    const expiringMs = 22 * 60 * 60 * 1000;

    if (elapsedMs >= dayMs) return 'expired';
    if (elapsedMs >= expiringMs) return 'expiring';
    return 'available';
  }

  replyWindowTooltip(timestamp: string): string {
    const state = this.replyWindowState(timestamp);
    if (state === 'expired') return 'Past 24 hours. Next reply is billable.';
    if (state === 'expiring') return '24-hour window is about to expire.';
    return 'Within 24-hour window. Reply is free-form.';
  }
}
